/**
 * Read-safe Repository over the Canonical Synthetic Dataset
 * Returns deep clones so UI mutations cannot mutate the baseline dataset.
 */
import {
  CANONICAL_PROGRAM,
  CANONICAL_RISKS,
  CANONICAL_SIGNALS,
  CANONICAL_EVIDENCE,
  CANONICAL_DECISIONS,
  CANONICAL_WORKFLOW_STAGES,
  CANONICAL_WORK_TASKS,
  CANONICAL_KNOWLEDGE_DOCUMENTS,
  CANONICAL_AGENTS,
  CANONICAL_CANVAS_OBJECTS,
  CANONICAL_CANVAS_ACTIVITY,
  CANONICAL_EXECUTION_GRAPH,
} from './demoDataset';

const clone = (obj) => JSON.parse(JSON.stringify(obj));

export function getDemoProgram() {
  return clone(CANONICAL_PROGRAM);
}

export function getDemoBoardroomOverview() {
  const risks = clone(CANONICAL_RISKS);
  const signals = clone(CANONICAL_SIGNALS);
  const criticalCount = risks.filter((r) => r.severity === 'CRITICAL').length;
  const highCount = risks.filter((r) => r.severity === 'HIGH').length;

  return {
    program_id: CANONICAL_PROGRAM.id,
    program_name: CANONICAL_PROGRAM.name,
    risk_index: { value: 3.02, trend: 'stable', active: 2, total: risks.length },
    risk_trend: [
      { date: 'Aug 26', value: 2.72 },
      { date: 'Aug 27', value: 2.88 },
      { date: 'Aug 28', value: 3.02 },
    ],
    heatmap: {
      risk_alignment: {
        public_ref: 'risk_alignment',
        label: 'Strategic Alignment',
        level: 'HIGH',
        count: 1,
        state: 'CONFIRMED',
      },
      risk_dependency: {
        public_ref: 'risk_dependency',
        label: 'Delivery Dependency',
        level: 'CRITICAL',
        count: 1,
        state: 'EMERGING',
      },
      risk_evidence_sufficiency: {
        public_ref: 'risk_evidence_sufficiency',
        label: 'Evidence Sufficiency',
        level: 'MEDIUM',
        count: 1,
        state: 'MONITORED',
      },
      risk_capacity: {
        public_ref: 'risk_capacity',
        label: 'Operational Capacity',
        level: 'MEDIUM',
        count: 1,
        state: 'CONFIRMED',
      },
    },
    risk_distribution: [
      { name: 'CRITICAL', value: criticalCount, percentage: 25 },
      { name: 'HIGH', value: highCount, percentage: 25 },
      { name: 'MEDIUM', value: 2, percentage: 50 },
    ],
    bottlenecks: [
      { id: 'risk_dependency', label: 'Delivery Dependency', percentage: 50 },
      { id: 'risk_alignment', label: 'Strategic Alignment', percentage: 50 },
    ],
    recent_validated_signals: signals,
    climate_signals: [],
    execution_readiness_summary: {
      state: 'READY_WITH_CONDITIONS',
      conditions: ['Evidence coverage', 'Human validation', 'Critical containment'],
      reasons: ['One critical vendor delivery dependency remains under active mitigation.'],
    },
    boardroom_projection: {
      readiness_state: 'READY_WITH_CONDITIONS',
      evidence_coverage: 86,
      evidence_sufficiency: { state: 'PARTIAL' },
      validated_count: signals.length,
      recent_total: signals.length,
      conditions_met: 2,
      conditions_total: 3,
      red_team_pending: 1,
      human_validation_state: 'VALIDATED',
      conditions: [
        { label: 'Evidence coverage', met: true },
        { label: 'Human validation', met: true },
        { label: 'Critical containment', met: false },
      ],
      readiness_dimensions: [
        { name: 'Strategic alignment' },
        { name: 'Delivery dependency' },
      ],
    },
  };
}

export function getDemoWorkflowStages() {
  return clone(CANONICAL_WORKFLOW_STAGES);
}

export function getDemoWorkTasks() {
  return clone(CANONICAL_WORK_TASKS);
}

export function getDemoDecisions() {
  return clone(CANONICAL_DECISIONS);
}

export function getDemoRisks() {
  return clone(CANONICAL_RISKS);
}

export function getDemoSignals() {
  return clone(CANONICAL_SIGNALS);
}

export function getDemoEvidence() {
  return clone(CANONICAL_EVIDENCE);
}

export function getDemoKnowledgeDocuments() {
  return clone(CANONICAL_KNOWLEDGE_DOCUMENTS);
}

export function getDemoAgents() {
  return clone(CANONICAL_AGENTS);
}

export function getDemoCanvasObjects() {
  return clone(CANONICAL_CANVAS_OBJECTS);
}

export function getDemoCanvasActivity() {
  return clone(CANONICAL_CANVAS_ACTIVITY);
}

export function getDemoExecutionGraph() {
  return clone(CANONICAL_EXECUTION_GRAPH);
}

export function getDemoEnterprisePosture() {
  return {
    sovereignty: { score: 100, level: 'SOVEREIGN_AIR_GAPPED', details: 'Local on-premise execution with zero external telemetry or LLM leakage.' },
    data_residency: { jurisdiction: 'EU / EEA', storage: 'Tenant-Encrypted PostgreSQL 16', backup_verified: true },
    compliance_controls: [
      { standard: 'ISO/IEC 27001 Alignment', status: 'CONTROL_ALIGNED', evidence_ref: 'demo-evi-call-fit' },
      { standard: 'Four-Eyes Decision Boundary', status: 'SUPPORTED', evidence_ref: 'demo-evi-steering-record' },
      { standard: 'Air-Gap Zero WAN Egress', status: 'VERIFIED', evidence_ref: 'demo-evi-call-fit' },
    ],
    governed_agents_count: CANONICAL_AGENTS.length,
    active_programs_count: 1,
    dimensions: [
      { id: 'data_sovereignty', title: 'Data Sovereignty', summary: '100% tenant-isolated data boundary with local deterministic storage.', state: 'enforced', source: 'Sovereign Core' },
      { id: 'four_eyes_approval', title: 'Four-Eyes Principle', summary: 'Dual stakeholder approval enforced on all critical stage-gate decisions.', state: 'enforced', source: 'Governance Policy' },
      { id: 'airgap_telemetry', title: 'Air-Gap Telemetry', summary: 'Zero WAN data leakage. All AI inference executed within sovereign perimeter.', state: 'enforced', source: 'Runtime Monitor' },
      { id: 'audit_provenance', title: 'Audit Trail Provenance', summary: 'Immutable SHA-256 hash chaining on all decision and evidence transactions.', state: 'operational', source: 'Ledger Audit' },
    ],
  };
}

